import { ChevronLeft as IconChevronLeft, ChevronRight as IconChevronRight } from "@mui/icons-material";
import { Button, Card, IconButton, Popover, Stack } from "@mui/material";
import { CalendarIcon, DateCalendar } from "@mui/x-date-pickers";
import { addDays, subDays } from "date-fns";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { formatDate } from "~/modules/app/util/formatters";

interface MapDatepickerProps {
    currentDate: Date;
    onDateChange: (newDate: Date) => void;
}

export function MapDatepicker({ currentDate, onDateChange }: MapDatepickerProps) {
    const { t } = useTranslation("plannerV2");
    const [popoverAnchorEl, setPopoverAnchorEl] = useState<HTMLSpanElement | null>(null);
    const formattedCurrentDate = formatDate(currentDate, "long");

    function onMoveDateForwardClick() {
        onDateChange(addDays(currentDate, 1));
    }

    function onMoveDateBackwardClick() {
        onDateChange(subDays(currentDate, 1));
    }

    function handleDateSelect(newDate: Date | null) {
        if (newDate) {
            onDateChange(newDate);
            setPopoverAnchorEl(null);
        }
    }

    return (
        <>
            <Card
                component={Stack}
                sx={{ padding: 1 }}
                direction="row"
                alignItems="center"
                justifyContent="space-between"
            >
                <IconButton onClick={onMoveDateBackwardClick} aria-label={t("mapDatepicker.previousDay")}>
                    <IconChevronLeft />
                </IconButton>
                <Button
                    startIcon={<CalendarIcon />}
                    onClick={event => setPopoverAnchorEl(event.currentTarget)}
                    aria-label={t("mapDatepicker.openCalendar")}
                    sx={theme => ({
                        color: theme.palette.text.primary,
                        textTransform: "none",
                        fontSize: "1rem",
                    })}
                >
                    {formattedCurrentDate}
                </Button>
                <IconButton onClick={onMoveDateForwardClick} aria-label={t("mapDatepicker.nextDay")}>
                    <IconChevronRight />
                </IconButton>
            </Card>
            <Popover
                open={Boolean(popoverAnchorEl)}
                anchorEl={popoverAnchorEl}
                onClose={() => setPopoverAnchorEl(null)}
                anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "center",
                }}
                transformOrigin={{
                    vertical: "top",
                    horizontal: "center",
                }}
            >
                <DateCalendar value={currentDate} onChange={handleDateSelect} />
            </Popover>
        </>
    );
}
